import React, { useState, useEffect } from "react";
import WeekTaskModal from "./WeekTaskModal";

const LifeGrid = ({ weeksLeft, birthDate }) => {
  const totalYears = 60;
  const weeksPerYear = 52;
  const passedWeeks = totalYears * weeksPerYear - weeksLeft;
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [weeksWithTasks, setWeeksWithTasks] = useState(new Set());

  // Check which weeks have tasks
  useEffect(() => {
    const weeksWithTasksSet = new Set();
    for (let i = 0; i < totalYears * weeksPerYear; i++) {
      const storageKey = `week_tasks_${i}`;
      const tasks = localStorage.getItem(storageKey);
      if (tasks) {
        try {
          const parsedTasks = JSON.parse(tasks);
          if (parsedTasks && parsedTasks.length > 0) {
            weeksWithTasksSet.add(i);
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    }
    setWeeksWithTasks(weeksWithTasksSet);
  }, [selectedWeek, totalYears, weeksPerYear]);

  const handleWeekClick = (weekNumber) => {
    setSelectedWeek(weekNumber);
  };

  const handleCloseModal = () => {
    setSelectedWeek(null);
    // Refresh weeks with tasks after closing
    refreshWeeksWithTasks();
  };

  const refreshWeeksWithTasks = () => {
    const weeksWithTasksSet = new Set();
    for (let i = 0; i < totalYears * weeksPerYear; i++) {
      const storageKey = `week_tasks_${i}`;
      const tasks = localStorage.getItem(storageKey);
      if (tasks) {
        try {
          const parsedTasks = JSON.parse(tasks);
          // Only mark as having tasks if array has items
          if (Array.isArray(parsedTasks) && parsedTasks.length > 0) {
            weeksWithTasksSet.add(i);
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    }
    setWeeksWithTasks(weeksWithTasksSet);
  };

  const handleTasksUpdate = (weekNumber, hasTasks) => {
    // Update the specific week's status
    const weeksWithTasksSet = new Set(weeksWithTasks);
    if (hasTasks) {
      weeksWithTasksSet.add(weekNumber);
    } else {
      weeksWithTasksSet.delete(weekNumber);
    }
    setWeeksWithTasks(weeksWithTasksSet);
  };

  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-4 mt-8 px-4">
        {[...Array(totalYears)].map((_, yearIndex) => (
          <div
            key={yearIndex}
            className="border border-gray-500 p-2 rounded-lg shadow-md bg-gradient-to-b from-gray-900 to-gray-800"
          >
            <div className="text-center text-xs sm:text-sm font-bold text-white mb-3">
              Year {yearIndex + 1}
            </div>
            {/* Adjust grid for smaller screens */}
            <div className="grid grid-cols-6 sm:grid-cols-8 md:grid-cols-13 gap-1">
              {[...Array(weeksPerYear)].map((_, weekIndex) => {
                const weekNumber = yearIndex * weeksPerYear + weekIndex;
                const hasTasks = weeksWithTasks.has(weekNumber);
                const isPassed = weekNumber < passedWeeks;
                
                return (
                  <div
                    key={weekIndex}
                    onClick={() => handleWeekClick(weekNumber)}
                    className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full transition-all duration-200 cursor-pointer relative ${
                      isPassed
                        ? hasTasks
                          ? "bg-yellow-500 hover:bg-yellow-400"
                          : "bg-red-500 hover:bg-red-400"
                        : hasTasks
                        ? "bg-green-500 hover:bg-green-400"
                        : "bg-gray-300 hover:bg-gray-200"
                    } ${hasTasks ? "ring-2 ring-white ring-opacity-50" : ""}`}
                    title={`Week ${weekNumber + 1} - ${
                      isPassed ? "Passed" : "Remaining"
                    }${hasTasks ? " (Has tasks)" : ""}`}
                  >
                    {hasTasks && (
                      <span className="absolute -top-1 -right-1 text-[8px] sm:text-[10px]">
                        ✨
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      
      {selectedWeek !== null && (
        <WeekTaskModal
          weekNumber={selectedWeek}
          isOpen={true}
          onClose={handleCloseModal}
          birthDate={birthDate}
          onTasksUpdate={handleTasksUpdate}
        />
      )}
    </>
  );
};

export default LifeGrid;
